import * as React from "react"
import { motion } from "motion/react"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import confetti from "canvas-confetti"
import { useSound } from "@/hooks/useSound"
import ovalPillImg from "@/public/oval-pill.png"
import spinWhooshSfx from "@/public/sounds/spin-whoosh.wav"
import wheelTickSfx from "@/public/sounds/wheel-tick.wav"
import winJingleSfx from "@/public/sounds/win-jingle.wav"
import bonusRevealSfx from "@/public/sounds/bonus-reveal.wav"
import sparkleLottie from "@/public/lotties/sparkle-overlay.lottie"
import confettiLottie from "@/public/lotties/confetti-celebration.lottie"
import starsGoldLottie from "@/public/lotties/stars-gold-burst.lottie"

const BONUS_WHEEL_SPIN_MS = 7600

export type BonusWheelSegment = {
  label: string
  amount: number
  colorA: string
  colorB: string
}

export const BONUS_WHEEL_SEGMENTS: BonusWheelSegment[] = [
  { label: "$1", amount: 1, colorA: "#0ea5a8", colorB: "#06b6d4" },
  { label: "$2", amount: 2, colorA: "#10b981", colorB: "#22c55e" },
  { label: "$5", amount: 5, colorA: "#f59e0b", colorB: "#f97316" },
  { label: "$1", amount: 1, colorA: "#8b5cf6", colorB: "#a855f7" },
  { label: "$10", amount: 10, colorA: "#f43f5e", colorB: "#fb7185" },
  { label: "$2", amount: 2, colorA: "#14b8a6", colorB: "#06b6d4" },
  { label: "$1", amount: 1, colorA: "#eab308", colorB: "#f59e0b" },
  { label: "$5", amount: 5, colorA: "#f97316", colorB: "#ef4444" },
  { label: "$2", amount: 2, colorA: "#22c55e", colorB: "#16a34a" },
  { label: "$1", amount: 1, colorA: "#3b82f6", colorB: "#6366f1" },
  { label: "$5", amount: 5, colorA: "#fb923c", colorB: "#f97316" },
  { label: "$2", amount: 2, colorA: "#06b6d4", colorB: "#0891b2" },
]

const NUM_SEGMENTS = BONUS_WHEEL_SEGMENTS.length
const SEGMENT_ANGLE = 360 / NUM_SEGMENTS
const NUM_LIGHTS = 24
const LIGHT_COLORS = ["#FFD700", "#F37020", "#00CED1"]

/* ── SVG helpers ──────────────────────────────────────────────────────── */

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const rad = ((angleDeg - 90) * Math.PI) / 180
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) }
}

function segmentPath(index: number, cx: number, cy: number, r: number) {
  const startAngle = index * SEGMENT_ANGLE
  const endAngle = (index + 1) * SEGMENT_ANGLE
  const start = polarToCartesian(cx, cy, r, startAngle)
  const end = polarToCartesian(cx, cy, r, endAngle)
  const largeArc = SEGMENT_ANGLE > 180 ? 1 : 0
  return `M${cx},${cy} L${start.x},${start.y} A${r},${r} 0 ${largeArc} 1 ${end.x},${end.y} Z`
}

/* ── Component ────────────────────────────────────────────────────────── */

interface BonusRoundWheelProps {
  amount: number
  onComplete: () => void
}

export function BonusRoundWheel({ amount, onComplete }: BonusRoundWheelProps) {
  const [phase, setPhase] = React.useState<"ready" | "spinning" | "done">("ready")
  const [rotation, setRotation] = React.useState(0)
  const [winningIndex, setWinningIndex] = React.useState<number | null>(null)
  const completeTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const tickTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const wheelContainerRef = React.useRef<HTMLDivElement>(null)

  // Sounds
  const { play: playWhoosh } = useSound(spinWhooshSfx, { volume: 0.6 })
  const { play: playTick } = useSound(wheelTickSfx, { volume: 0.3 })
  const { play: playJingle } = useSound(winJingleSfx, { volume: 0.7 })
  const { play: playReveal } = useSound(bonusRevealSfx, { volume: 0.6 })

  React.useEffect(() => {
    return () => {
      if (completeTimerRef.current) clearTimeout(completeTimerRef.current)
      if (tickTimerRef.current) clearTimeout(tickTimerRef.current)
    }
  }, [])

  // Auto-scroll wheel into view when it appears
  React.useEffect(() => {
    const timer = setTimeout(() => {
      wheelContainerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 300)
    return () => clearTimeout(timer)
  }, [])

  // Decelerating tick sound during spin
  const startTickSounds = React.useCallback(() => {
    let interval = 80
    const maxInterval = 600
    const multiplier = 1.08
    const spinEnd = Date.now() + BONUS_WHEEL_SPIN_MS

    function scheduleTick() {
      if (Date.now() >= spinEnd) return
      playTick()
      interval = Math.min(interval * multiplier, maxInterval)
      tickTimerRef.current = setTimeout(scheduleTick, interval)
    }
    scheduleTick()
  }, [playTick])

  const spin = () => {
    if (phase !== "ready") return

    const matching = BONUS_WHEEL_SEGMENTS
      .map((segment, index) => ({ segment, index }))
      .filter(({ segment }) => segment.amount === amount)
      .map(({ index }) => index)

    const targetIndex = matching[Math.floor(Math.random() * matching.length)] ?? 0
    const targetCenter = targetIndex * SEGMENT_ANGLE + SEGMENT_ANGLE / 2
    const currentNormalized = ((rotation % 360) + 360) % 360
    const targetNormalized = (360 - targetCenter) % 360
    let delta = targetNormalized - currentNormalized
    if (delta < 0) delta += 360

    const extraTurns = (7 + Math.floor(Math.random() * 3)) * 360
    setWinningIndex(targetIndex)
    setPhase("spinning")
    setRotation((prev) => prev + extraTurns + delta)

    // Sounds
    playWhoosh()
    startTickSounds()

    completeTimerRef.current = setTimeout(() => {
      setPhase("done")
      playJingle()
      setTimeout(() => playReveal(), 600)
      confetti({
        particleCount: 90,
        spread: 65,
        origin: { y: 0.6 },
        colors: ["#00CED1", "#FFD700", "#32CD32", "#F37020"],
      })
      onComplete()
    }, BONUS_WHEEL_SPIN_MS + 150)
  }

  const cx = 50,
    cy = 50,
    outerR = 46

  return (
    <div ref={wheelContainerRef} className="mt-4 rounded-2xl border-2 border-[#2A2A45] bg-[#121220] p-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3">
        <div>
          <p
            className="text-[11px] uppercase tracking-[0.2em] text-[#00CED1] font-black"
            style={{ fontFamily: "Bangers, cursive" }}
          >
            Bonus Round
          </p>
          <h5 className="text-white font-black text-base" style={{ fontFamily: "Bangers, cursive", letterSpacing: "0.04em" }}>
            Spin the Oval Wheel
          </h5>
          <p className="text-[#9A9ABF] text-xs">Your bonus cash is locked. The wheel reveals the amount.</p>
        </div>
        <div className="rounded-xl border border-[#00CED1]/30 bg-[#00CED1]/10 px-3 py-2">
          <span className="text-[#00CED1] text-xs font-black tracking-wide" style={{ fontFamily: "Bangers, cursive" }}>
            TARGET BONUS
          </span>
        </div>
      </div>

      {/* Wheel container */}
      <div
        className={`relative mx-auto w-full max-w-[320px] aspect-square ${phase === "ready" ? "cursor-pointer" : ""}`}
        onClick={phase === "ready" ? spin : undefined}
        role={phase === "ready" ? "button" : undefined}
        tabIndex={phase === "ready" ? 0 : -1}
        aria-label={phase === "ready" ? "Spin bonus wheel" : undefined}
        onKeyDown={(e) => {
          if (phase !== "ready") return
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            spin()
          }
        }}
      >
        {/* Sparkle overlay during spin */}
        {phase === "spinning" && (
          <div className="absolute inset-0 z-30 pointer-events-none opacity-60">
            <DotLottieReact src={sparkleLottie} loop autoplay style={{ width: "100%", height: "100%" }} />
          </div>
        )}

        {/* Confetti overlay on win */}
        {phase === "done" && (
          <div className="absolute inset-0 z-30 pointer-events-none">
            <DotLottieReact src={confettiLottie} autoplay style={{ width: "100%", height: "100%" }} />
          </div>
        )}

        {/* Premium pointer */}
        <div className="absolute left-1/2 -top-1 -translate-x-1/2 z-20 flex flex-col items-center">
          <svg width="36" height="36" viewBox="0 0 36 36">
            <defs>
              <linearGradient id="pointerGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#FFD700" />
                <stop offset="50%" stopColor="#F37020" />
                <stop offset="100%" stopColor="#CC5500" />
              </linearGradient>
              <filter id="pointerGlow">
                <feGaussianBlur stdDeviation="3" result="glow" />
                <feMerge>
                  <feMergeNode in="glow" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            <polygon
              points="18,32 6,4 30,4"
              fill="url(#pointerGrad)"
              stroke="#1A1A30"
              strokeWidth="1.5"
              filter="url(#pointerGlow)"
            />
            {/* White highlight reflection */}
            <polygon points="18,28 13,8 19,6" fill="rgba(255,255,255,0.25)" />
          </svg>
        </div>

        {/* Outer metallic ring with glow */}
        <div
          className="absolute inset-0 rounded-full p-[10px]"
          style={{
            background: "linear-gradient(180deg, #5A5A75 0%, #2A2A45 25%, #4A4A65 50%, #1A1A30 75%, #5A5A75 100%)",
            boxShadow: "0 0 30px rgba(0,206,209,0.15), 0 0 60px rgba(0,0,0,0.5)",
          }}
        >
          {/* Dark stroke ring */}
          <div className="absolute inset-0 rounded-full border-2 border-[#0f0f1f]" />

          {/* Chase lights */}
          <div className="absolute inset-0 pointer-events-none z-10">
            {Array.from({ length: NUM_LIGHTS }, (_, i) => {
              const color = LIGHT_COLORS[i % 3]
              return (
                <motion.span
                  key={i}
                  className="absolute rounded-full"
                  style={{
                    width: 10,
                    height: 10,
                    left: "50%",
                    top: "50%",
                    marginLeft: -5,
                    marginTop: -5,
                    transform: `rotate(${(i * 360) / NUM_LIGHTS}deg) translateY(-${
                      // adapt to container size
                      148
                    }px)`,
                    transformOrigin: "center center",
                    background: color,
                    boxShadow:
                      phase === "spinning"
                        ? `0 0 8px 3px ${color}, 0 0 16px 4px ${color}55`
                        : `0 0 4px 1px ${color}44`,
                  }}
                  animate={{
                    opacity: phase === "spinning" ? [0.2, 1, 0.2] : [0.35, 0.7, 0.35],
                    scale: phase === "spinning" ? [0.85, 1.2, 0.85] : [0.95, 1.05, 0.95],
                  }}
                  transition={{
                    duration: phase === "spinning" ? 0.15 : 1.4,
                    repeat: Infinity,
                    delay: i * (phase === "spinning" ? 0.025 : 0.05),
                  }}
                />
              )
            })}
          </div>

          {/* Spinning wheel (SVG) */}
          <motion.div
            className="relative w-full h-full rounded-full overflow-hidden"
            animate={{ rotate: rotation }}
            transition={{ duration: BONUS_WHEEL_SPIN_MS / 1000, ease: [0.08, 0.8, 0.15, 1] }}
            style={{ pointerEvents: "none" }}
          >
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100">
              <defs>
                {/* Per-segment gradients */}
                {BONUS_WHEEL_SEGMENTS.map((seg, i) => {
                  const midAngle = i * SEGMENT_ANGLE + SEGMENT_ANGLE / 2 - 90
                  const rad = (midAngle * Math.PI) / 180
                  const x1p = 50 + 30 * Math.cos(rad)
                  const y1p = 50 + 30 * Math.sin(rad)
                  const x2p = 50 - 10 * Math.cos(rad)
                  const y2p = 50 - 10 * Math.sin(rad)
                  return (
                    <linearGradient
                      key={`grad-${i}`}
                      id={`seg-grad-${i}`}
                      x1={x1p}
                      y1={y1p}
                      x2={x2p}
                      y2={y2p}
                      gradientUnits="userSpaceOnUse"
                    >
                      <stop offset="0%" stopColor={seg.colorA} />
                      <stop offset="100%" stopColor={seg.colorB} />
                    </linearGradient>
                  )
                })}
                {/* Inner shadow filter */}
                <filter id="segShadow">
                  <feComponentTransfer in="SourceAlpha">
                    <feFuncA type="table" tableValues="1 0" />
                  </feComponentTransfer>
                  <feGaussianBlur stdDeviation="1.5" />
                  <feOffset dx="0" dy="0.5" result="shadow" />
                  <feFlood floodColor="#000000" floodOpacity="0.25" result="color" />
                  <feComposite in2="shadow" operator="in" />
                  <feComposite in2="SourceGraphic" operator="over" />
                </filter>
              </defs>

              {/* Segments */}
              {BONUS_WHEEL_SEGMENTS.map((_, i) => (
                <path
                  key={`seg-${i}`}
                  d={segmentPath(i, cx, cy, outerR)}
                  fill={`url(#seg-grad-${i})`}
                  filter="url(#segShadow)"
                />
              ))}

              {/* Separator lines */}
              {BONUS_WHEEL_SEGMENTS.map((_, i) => {
                const angle = (i * SEGMENT_ANGLE - 90) * (Math.PI / 180)
                const x2 = cx + outerR * Math.cos(angle)
                const y2 = cy + outerR * Math.sin(angle)
                return (
                  <line
                    key={`line-${i}`}
                    x1={cx}
                    y1={cy}
                    x2={x2}
                    y2={y2}
                    stroke="rgba(255,255,255,0.15)"
                    strokeWidth="0.35"
                  />
                )
              })}

              {/* Segment labels — rotated radially using Bangers font */}
              {BONUS_WHEEL_SEGMENTS.map((seg, i) => {
                const midAngle = i * SEGMENT_ANGLE + SEGMENT_ANGLE / 2
                const labelR = outerR * 0.65
                const pos = polarToCartesian(cx, cy, labelR, midAngle)
                return (
                  <text
                    key={`label-${i}`}
                    x={pos.x}
                    y={pos.y}
                    textAnchor="middle"
                    dominantBaseline="central"
                    transform={`rotate(${midAngle}, ${pos.x}, ${pos.y})`}
                    fill="white"
                    fontSize="5.5"
                    fontFamily="Bangers, cursive"
                    fontWeight="900"
                    style={{ textShadow: "0 1px 3px rgba(0,0,0,0.7)" }}
                  >
                    <tspan filter="url(#textShadow)">{seg.label}</tspan>
                  </text>
                )
              })}
            </svg>

            {/* Inner ring line */}
            <div className="absolute inset-[4px] rounded-full border border-white/8 pointer-events-none" />
          </motion.div>
        </div>

        {/* Center hub */}
        <div className="absolute inset-[28%] z-10 rounded-full flex items-center justify-center pointer-events-none">
          {/* Outer metallic ring */}
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: "linear-gradient(180deg, #4A4A65 0%, #2A2A45 40%, #3A3A55 60%, #1A1A30 100%)",
              boxShadow: "0 0 20px rgba(0,0,0,0.5)",
            }}
          />
          {/* Inner dark gradient */}
          <div className="absolute inset-[3px] rounded-full bg-gradient-to-br from-[#252540] via-[#1C1C34] to-[#14142A] border border-white/10" />
          {/* Specular highlight */}
          <div className="absolute inset-[3px] rounded-full bg-[radial-gradient(circle_at_35%_30%,rgba(255,255,255,0.15),transparent_50%)]" />
          <motion.img
            src={ovalPillImg}
            alt="Oval mascot"
            className="relative w-[80%] h-[80%] object-contain drop-shadow-[0_0_12px_rgba(0,206,209,0.4)]"
            animate={phase === "ready" ? { scale: [1, 1.05, 1] } : { scale: 1 }}
            transition={phase === "ready" ? { duration: 2, repeat: Infinity, ease: "easeInOut" } : { duration: 0.2 }}
          />
        </div>
      </div>

      {/* Bottom CTA / status */}
      <div className="mt-3">
        {phase === "ready" && (
          <button
            type="button"
            onClick={spin}
            className="relative z-40 w-full px-4 py-3 rounded-xl border-2 border-[#2A2A45] text-white font-black cursor-pointer"
            style={{
              background: "linear-gradient(135deg, #00CED1, #0ea5a8)",
              fontFamily: "Bangers, cursive",
              letterSpacing: "0.06em",
              fontSize: "1.05rem",
            }}
          >
            Spin Bonus Wheel
          </button>
        )}
        {phase === "spinning" && (
          <div
            className="w-full px-4 py-3 rounded-xl border-2 border-[#2A2A45] bg-[#151528] text-center text-white font-black"
            style={{ fontFamily: "Bangers, cursive", letterSpacing: "0.04em" }}
          >
            Wheel spinning... big reveal incoming
          </div>
        )}
        {phase === "done" && (
          <div className="relative w-full px-4 py-3 rounded-xl border-2 border-[#00CED1]/30 bg-[#00CED1]/10 text-center overflow-hidden">
            {/* Gold stars burst behind the amount */}
            <div className="absolute inset-0 z-0 pointer-events-none flex items-center justify-center">
              <div className="w-32 h-32">
                <DotLottieReact src={starsGoldLottie} autoplay style={{ width: "100%", height: "100%" }} />
              </div>
            </div>
            <div className="relative z-10">
              <p
                className="text-[#00CED1] text-xs uppercase tracking-[0.2em] font-black"
                style={{ fontFamily: "Bangers, cursive" }}
              >
                Bonus Revealed
              </p>
              <p className="text-white font-black text-3xl mt-1" style={{ fontFamily: "Bangers, cursive" }}>
                ${amount}
              </p>
              {winningIndex !== null && (
                <p className="text-[#9A9ABF] text-xs mt-1">Landed on {BONUS_WHEEL_SEGMENTS[winningIndex]?.label}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
